import React from "react";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";

import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Box,
  FormGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  SkuContentRule,
  Skus,
  ISkuContentRuleRequest,
  ListResponseOfSkuContentRuleType,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import Field from "components/Field";
import Loader from "components/Loader";
import SegmentedToggle from "components/SegmentedToggle";

const useStyles = makeStyles({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

const { Form } = withTypes<ISkuContentRuleRequest>();

const SKU_ACCESS_TYPE_IDS = {
  open: "4e14298b-758e-4020-b5f9-537b60e98a14",
  gated: "71dfd6b8-90dd-49ff-b0ba-6adb53571fdc",
} as const;

type SkuRuleFormProps = {
  onSubmit: (values: ISkuContentRuleRequest) => void;
  onCancel?: () => void;
  sku: Skus;
  skuRule?: SkuContentRule;
  title: string;
  submitLabel: string;
  isEdit?: boolean;
  isMembershipGatedAccessEnabled: boolean;
};

const SkuRuleForm = ({
  onCancel,
  onSubmit,
  sku,
  skuRule,
  title,
  submitLabel,
  isEdit,
  isMembershipGatedAccessEnabled,
}: SkuRuleFormProps & FormProps<ISkuContentRuleRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { skuService } = useGlobalContext();

  const {
    isLoading,
    data,
    isError,
    error,
  } = useQuery<ListResponseOfSkuContentRuleType>("skuRuleTypes", () =>
    skuService.getSkuContentRuleTypes()
  );

  const init = {
    id: skuRule?.id,
    skuId: sku.id,
    name: skuRule?.name ?? "",
    skuContentRuleTypeId: skuRule?.ruleType?.id,
    excludeFilter: skuRule?.excludeFilter ?? false,
    sequence: skuRule?.sequence,
    skuAccessTypeId: skuRule?.skuAccessTypeId ?? SKU_ACCESS_TYPE_IDS.open,
  };

  if (isLoading) return <Loader />;
  if (isError) {
    console.log(error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRuleTypeError" />
        </Alert>
      </Box>
    );
  }

  const rules = data?.data ?? [];

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values, form }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "ruleNameLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {!isEdit && (
                <Grid item xs={12}>
                  <Typography variant="body2" mb={1}>
                    {t({ id: "skuRuleTypeTitle" })}
                  </Typography>
                  <FormGroup>
                    {rules.map((r) => (
                      <Field
                        key={r.id}
                        name="skuContentRuleTypeId"
                        value={r.id}
                        type="radio"
                        required
                      >
                        {({ input }) => (
                          <FormControlLabel
                            {...input}
                            control={<Radio data-cy={`${r.name}RadioButton`} />}
                            label={t(
                              { id: "skuRuleTypeLabel" },
                              { name: r.name }
                            )}
                          />
                        )}
                      </Field>
                    ))}
                  </FormGroup>
                </Grid>
              )}

              <Field name="excludeFilter">
                {({ input }) => (
                  <Grid item xs={12}>
                    <SegmentedToggle
                      label={t({ id: "skuRuleBehaviorLabel" })}
                      value={input.value}
                      onChange={(newValue) => {
                        input.onChange(newValue);
                        if (newValue === true) {
                          form.change("skuAccessTypeId", SKU_ACCESS_TYPE_IDS.open);
                        }
                      }}
                      options={[
                        {
                          value: false,
                          label: t({ id: "skuRuleIncludeLabel" }),
                          activeColor: "success",
                          hint: t({ id: "skuRuleIncludeHint" }),
                        },
                        {
                          value: true,
                          label: t({ id: "skuRuleExcludeLabel" }),
                          activeColor: "error",
                          hint: t({ id: "skuRuleExcludeHint" }),
                        },
                      ]}
                    />
                  </Grid>
                )}
              </Field>

              {!values.excludeFilter && (
                <Field name="skuAccessTypeId">
                  {({ input }) => (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <Typography variant="body2">
                          {t({ id: "skuRuleAccessTypeLabel" })}
                        </Typography>
                        {!isMembershipGatedAccessEnabled && (
                          <Tooltip
                            title={t({ id: "skuRuleAccessTypeDisabledTooltip" })}
                            placement="right"
                          >
                            <InfoOutlinedIcon fontSize="small" color="disabled" />
                          </Tooltip>
                        )}
                      </Box>
                      <SegmentedToggle
                        value={input.value}
                        onChange={input.onChange}
                        disabled={!isMembershipGatedAccessEnabled}
                        options={[
                          {
                            value: SKU_ACCESS_TYPE_IDS.open,
                            label: t({ id: "skuRuleAccessTypeOpenLabel" }),
                            activeColor: "success",
                            hint: t({ id: "skuRuleAccessTypeOpenHint" }),
                          },
                          {
                            value: SKU_ACCESS_TYPE_IDS.gated,
                            label: t({ id: "skuRuleAccessTypeGatedLabel" }),
                            activeColor: "warning",
                            hint: t({ id: "skuRuleAccessTypeGatedHint" }),
                          },
                        ]}
                      />
                    </Grid>
                  )}
                </Field>
              )}

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel} data-cy="cancelButton">
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting || !values.skuContentRuleTypeId}
              data-cy="submitButton"
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default SkuRuleForm;
